"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import { StemLeafChart } from "@/components/tool-kit/charts/stem-leaf-chart";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { sorted } from "@/lib/stats/descriptive";
import {
  MINUS,
  type NumberFormat,
  usesDecimalComma,
} from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { parseNumberTokens } from "@/lib/tool-kit/charts/parse";
import { stepDecimals } from "@/lib/tool-kit/charts/scale";
import {
  autoLeafUnit,
  buildStemLeaf,
  firstLeaf,
  type LeafRounding,
  MAX_STEMS,
  type StemLeafDisplay,
  stemLabel,
  stemLeafLines,
  stemLeafValue,
} from "@/lib/tool-kit/charts/stem-leaf";

/** English strings. A translated content file overrides these through `ui`. */
export const STEM_AND_LEAF_PLOT_GENERATOR_STRINGS = {
  dataLabel: "Your data — numbers separated by commas, spaces or new lines",
  secondDataLabel: "Second data set",
  dataHint:
    "Decimals and negative numbers work. If your numbers use decimal commas, separate them with semicolons.",
  exampleData:
    "12, 15, 21, 24, 24, 27, 30, 32, 33, 35, 38, 38, 41, 43, 47, 52, 56",
  exampleSecond:
    "18, 22, 25, 26, 29, 31, 34, 34, 36, 39, 40, 42, 44, 45, 48, 51, 53",
  exampleTitle: "Quiz Scores Out of 60",
  backToBack: "Compare two data sets back to back",
  firstName: "Name of the first set",
  secondName: "Name of the second set",
  firstDefault: "Class A",
  secondDefault: "Class B",
  unitLabel: "Stems and leaves",
  unitAuto: "Automatic",
  unitAutoChosen: "Automatic (leaf unit {unit})",
  unitHundreds: "Hundreds | tens (370 → 3 | 7)",
  unitTens: "Tens | ones (37 → 3 | 7)",
  unitOnes: "Ones | tenths (3.7 → 3 | 7)",
  unitTenths: "Tenths | hundredths (0.37 → 3 | 7)",
  split: "Split stems (each stem twice: leaves 0–4, then 5–9)",
  roundingLabel: "Digits beyond the leaf",
  truncate: "Cut off (truncate)",
  round: "Round",
  stemHeading: "Stem",
  leafHeading: "Leaf",
  keySingle: "Key: {stem} | {leaf} = {value}",
  keyBackToBack:
    "Key: {leftLeaf} | {leftStem} = {leftValue} in {leftName}; {rightStem} | {rightLeaf} = {rightValue} in {rightName}",
  textHeading: "Text version",
  orderedData: "Ordered data: {values}",
  orderedDataNamed: "Ordered data, {name}: {values}",
  conventionTruncate:
    "Digits finer than the leaf unit ({unit}) are cut off, not rounded — the usual convention, from Tukey.",
  conventionRound:
    "Digits finer than the leaf unit ({unit}) are rounded to the nearest leaf.",
  conventionNegative:
    "Negative values have their own stems, starting at −0 for values between −1 stem unit and 0. On every stem the leaves increase moving away from the stem, so −2 | 1 3 8 means −21, −23 and −28.",
  conventionSplit:
    "Split stems: each stem appears twice, leaves 0–4 on the first line and 5–9 on the second.",
  fewStems:
    "Only {count} stems — try split stems, or a smaller stem unit, to see the shape.",
  tooManyStems:
    "These values need more than {max} stems at this leaf unit. Choose a larger stem unit.",
  valuesDetected: "one:{count} value|other:{count} values",
  invalidTokens:
    "one:Left out {count} entry that is not a number: {tokens}.|other:Left out {count} entries that are not numbers: {tokens}.",
  enterData: "Enter at least one number to build the plot.",
  labelsAndStyle: "Labels and style",
  chartTitle: "Title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout. The key stays inside the image, where APA puts a figure's legend.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy as text",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  chartLabel: "Stem-and-leaf plot",
  chartDescription:
    "Stem-and-leaf plot of {values} on {stems} stems, from {min} to {max}.",
} as const;

type Strings = typeof STEM_AND_LEAF_PLOT_GENERATOR_STRINGS;
type UnitChoice = "auto" | "10" | "1" | "0.1" | "0.01";

/** The key under the display, built from the first leaf of each data set. */
function keyLine(
  display: StemLeafDisplay,
  names: [string, string],
  strings: Strings,
  fmt: NumberFormat
): string {
  const decimals = stepDecimals(display.leafUnit);
  // The stems print a typographic minus, so the value in the key does too.
  const value = (row: Parameters<typeof stemLeafValue>[0], leaf: number) =>
    fmt(stemLeafValue(row, leaf, display.leafUnit), {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).replace("-", MINUS);
  const first = firstLeaf(display, 0);
  if (!first) {
    return "";
  }
  if (display.rows[0]?.leaves.length !== 2) {
    return t(strings.keySingle, {
      stem: stemLabel(first.row),
      leaf: first.leaf,
      value: value(first.row, first.leaf),
    });
  }
  const second = firstLeaf(display, 1) ?? first;
  return t(strings.keyBackToBack, {
    leftLeaf: first.leaf,
    leftStem: stemLabel(first.row),
    leftValue: value(first.row, first.leaf),
    leftName: names[0],
    rightStem: stemLabel(second.row),
    rightLeaf: second.leaf,
    rightValue: value(second.row, second.leaf),
    rightName: names[1],
  });
}

/** The conventions that apply to this display, stated under it. */
function conventions(
  values: number[],
  leafUnit: number,
  rounding: LeafRounding,
  split: boolean,
  strings: Strings,
  fmt: NumberFormat
): string[] {
  const unit = fmt(leafUnit, { maximumFractionDigits: 6 });
  const notes = [
    t(
      rounding === "truncate"
        ? strings.conventionTruncate
        : strings.conventionRound,
      { unit }
    ),
  ];
  if (values.some((value) => value < 0)) {
    notes.push(strings.conventionNegative);
  }
  if (split) {
    notes.push(strings.conventionSplit);
  }
  return notes;
}

/** Guidance about the input: what was left out, and a display with too few stems. */
function stemNotices(
  all: number[],
  invalid: string[],
  display: StemLeafDisplay | null,
  split: boolean,
  strings: Strings,
  locale: string,
  fmt: NumberFormat
): string[] {
  const notes: string[] = [];
  if (all.length === 0) {
    notes.push(strings.enterData);
  }
  if (invalid.length > 0) {
    notes.push(
      tPlural(strings.invalidTokens, locale, invalid.length, {
        count: fmt(invalid.length),
        tokens: invalid.slice(0, 5).join(" "),
      })
    );
  }
  if (display && display.rows.length <= 2 && !split) {
    notes.push(t(strings.fewStems, { count: fmt(display.rows.length) }));
  }
  return notes;
}

export default function StemAndLeafPlotGeneratorWidget() {
  const strings = useToolUi(STEM_AND_LEAF_PLOT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleData);
  const [rawSecond, setRawSecond] = useState<string>(strings.exampleSecond);
  const [paired, setPaired] = useState(false);
  const [names, setNames] = useState<[string, string]>([
    strings.firstDefault,
    strings.secondDefault,
  ]);
  const [unit, setUnit] = useState<UnitChoice>("auto");
  const [split, setSplit] = useState(false);
  const [rounding, setRounding] = useState<LeafRounding>("truncate");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const first = parseNumberTokens(raw);
  const second = parseNumberTokens(rawSecond);
  const sets = paired ? [first.values, second.values] : [first.values];
  const all = sets.flat();
  const invalid = paired
    ? [...first.invalid, ...second.invalid]
    : first.invalid;
  const autoUnit = autoLeafUnit(all, { split });
  const leafUnit = unit === "auto" ? autoUnit : Number(unit);
  const display =
    all.length > 0 ? buildStemLeaf(sets, { leafUnit, split, rounding }) : null;
  const headings = paired
    ? [names[0], strings.stemHeading, names[1]]
    : [strings.stemHeading, strings.leafHeading];
  const key = display ? keyLine(display, names, strings, fmt) : "";
  const text = display
    ? [...stemLeafLines(display, { headings }), "", key].join("\n")
    : "";
  const listSeparator = usesDecimalComma(fmt) ? "; " : ", ";
  const ordered = sets.map((values, index) => ({
    name: paired ? names[index] : "",
    values: sorted(values)
      .map((v) => fmt(v, { maximumFractionDigits: 6 }).replace("-", MINUS))
      .join(listSeparator),
  }));
  const decimals = stepDecimals(leafUnit);
  const autoLabel = t(strings.unitAutoChosen, {
    unit: fmt(autoUnit, { maximumFractionDigits: 6 }),
  });

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.dataHint}
        id="slp-data"
        label={strings.dataLabel}
        onChange={setRaw}
        rows={4}
        value={raw}
      />
      <Checkbox
        checked={paired}
        label={strings.backToBack}
        onChange={setPaired}
      />
      {paired ? (
        <div className="space-y-4">
          <TextAreaField
            id="slp-data-second"
            label={strings.secondDataLabel}
            onChange={setRawSecond}
            rows={4}
            value={rawSecond}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="slp-name-first"
              label={strings.firstName}
              onChange={(value) => setNames([value, names[1]])}
              value={names[0]}
            />
            <TextField
              id="slp-name-second"
              label={strings.secondName}
              onChange={(value) => setNames([names[0], value])}
              value={names[1]}
            />
          </div>
        </div>
      ) : null}
      <div aria-live="polite" className="space-y-2">
        {all.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.valuesDetected, locale, all.length, {
              count: fmt(all.length),
            })}
          </p>
        ) : null}
        {all.length > 0 && !display ? (
          <ErrorNotice>
            {t(strings.tooManyStems, { max: fmt(MAX_STEMS) })}
          </ErrorNotice>
        ) : null}
        {stemNotices(all, invalid, display, split, strings, locale, fmt).map(
          (notice) => (
            <Notice key={notice}>{notice}</Notice>
          )
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="slp-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          options={[
            {
              value: "auto",
              label: all.length > 0 ? autoLabel : strings.unitAuto,
            },
            { value: "10", label: strings.unitHundreds },
            { value: "1", label: strings.unitTens },
            { value: "0.1", label: strings.unitOnes },
            { value: "0.01", label: strings.unitTenths },
          ]}
          value={unit}
        />
        <PillGroup
          label={strings.roundingLabel}
          name="slp-rounding"
          onChange={setRounding}
          options={[
            { value: "truncate", label: strings.truncate },
            { value: "round", label: strings.round },
          ]}
          value={rounding}
        />
      </div>
      <Checkbox checked={split} label={strings.split} onChange={setSplit} />
      {display ? (
        <ChartFigure
          apa={style.apa}
          data={text}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "stem-and-leaf-plot")}
          labels={strings}
          note={style.note}
          table={null}
          title={style.title}
        >
          <StemLeafChart
            description={t(strings.chartDescription, {
              values: tPlural(strings.valuesDetected, locale, all.length, {
                count: fmt(all.length),
              }),
              stems: fmt(display.rows.length),
              min: fmt(Math.min(...all), {
                maximumFractionDigits: decimals + 2,
              }),
              max: fmt(Math.max(...all), {
                maximumFractionDigits: decimals + 2,
              }),
            })}
            display={display}
            headings={headings}
            id="slp-chart"
            keyText={key}
            label={style.title.trim() || strings.chartLabel}
            title={style.apa ? "" : style.title}
          />
        </ChartFigure>
      ) : null}
      {display ? (
        <section aria-live="polite" className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.textHeading}</h3>
          <pre className="overflow-x-auto rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed">
            {text}
          </pre>
          {ordered.map((entry) => (
            <p className="text-foreground text-sm" key={entry.name || "single"}>
              {t(
                entry.name ? strings.orderedDataNamed : strings.orderedData,
                entry
              )}
            </p>
          ))}
          {conventions(all, leafUnit, rounding, split, strings, fmt).map(
            (note) => (
              <p className={HINT} key={note}>
                {note}
              </p>
            )
          )}
        </section>
      ) : null}
      <ChartStyleFields
        idPrefix="slp"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
